/**
 * Metric extraction — surfaces headline numbers for the KPI grid.
 *
 * Two sources:
 *  1. Structured nodes typed currency / currencyRange / number / percentage.
 *  2. Prose mining: founder summaries embed their metrics in sentences
 *     ("$8,000 in Monthly Recurring Revenue", "monthly burn is $45,000",
 *     "$2.5 million Seed round"), so a keyword-window scanner recovers them.
 */

import { formatCompactNumber, formatMoney } from "./format";
import type { TConcept, TSemanticNode, TSummaryMetric } from "./types";

/* ------------------------------------------------------------------ */
/* From structured nodes                                               */
/* ------------------------------------------------------------------ */

function rangeToDisplay(value: Record<string, unknown>): { display: string; numeric?: number } | null {
  const entries = Object.entries(value);
  const currencyRaw = entries.find(([k]) => k.toLowerCase().includes("currency"))?.[1];
  const currency = typeof currencyRaw === "string" ? currencyRaw : "USD";
  const min = entries.find(([k]) => k.toLowerCase().includes("min"))?.[1];
  const max = entries.find(([k]) => k.toLowerCase().includes("max"))?.[1];
  const minNum = typeof min === "number" ? min : null;
  const maxNum = typeof max === "number" ? max : null;
  if (minNum != null && maxNum != null) {
    return { display: `${formatMoney(minNum, currency)} – ${formatMoney(maxNum, currency)}`, numeric: maxNum };
  }
  if (minNum != null) return { display: `From ${formatMoney(minNum, currency)}`, numeric: minNum };
  if (maxNum != null) return { display: `Up to ${formatMoney(maxNum, currency)}`, numeric: maxNum };
  return null;
}

function collectMetricNodes(nodes: TSemanticNode[], out: TSummaryMetric[]): void {
  for (const node of nodes) {
    if (node.concept === "meta" || node.dataType === "empty") continue;

    if (node.dataType === "currencyRange" && node.value && typeof node.value === "object") {
      const range = rangeToDisplay(node.value as Record<string, unknown>);
      if (range) {
        out.push({
          id: node.path.join("."),
          label: node.label,
          display: range.display,
          concept: node.concept,
          numeric: range.numeric,
          importance: node.importance,
        });
      }
      continue; // children of a range are represented by the range itself
    }

    if (typeof node.value === "number") {
      if (node.dataType === "currency") {
        out.push({
          id: node.path.join("."),
          label: node.label,
          display: formatMoney(node.value),
          concept: node.concept,
          numeric: node.value,
          importance: node.importance,
        });
      } else if (node.dataType === "percentage") {
        const pct = node.value <= 1 ? node.value * 100 : node.value;
        out.push({
          id: node.path.join("."),
          label: node.label,
          display: `${Math.round(pct)}%`,
          concept: node.concept,
          numeric: pct,
          importance: node.importance,
        });
      } else if (node.dataType === "number" && node.importance >= 50) {
        out.push({
          id: node.path.join("."),
          label: node.label,
          display: formatCompactNumber(node.value),
          concept: node.concept,
          numeric: node.value,
          importance: node.importance,
        });
      }
    }

    if (node.children) collectMetricNodes(node.children, out);
  }
}

/* ------------------------------------------------------------------ */
/* From prose                                                          */
/* ------------------------------------------------------------------ */

type TMinedPattern = {
  id: string;
  label: string;
  concept: TConcept;
  keywords: RegExp;
  importance: number;
};

/** Concept keyword windows searched around each money mention. */
const MINED_PATTERNS: TMinedPattern[] = [
  { id: "mined.mrr", label: "MRR", concept: "revenue", keywords: /\b(mrr|monthly recurring revenue)\b/i, importance: 95 },
  { id: "mined.arr", label: "ARR", concept: "revenue", keywords: /\b(arr|annual recurring revenue)\b/i, importance: 94 },
  { id: "mined.burn", label: "Monthly Burn", concept: "financial", keywords: /\bburn\b/i, importance: 90 },
  { id: "mined.raise", label: "Target Raise", concept: "fundraising", keywords: /\b(raise|raising|round)\b/i, importance: 92 },
  { id: "mined.valuation", label: "Valuation", concept: "fundraising", keywords: /\bvaluation\b/i, importance: 88 },
];

const MONEY_MENTION = /\$\s?(\d+(?:[,.]\d+)*)\s*(billion|million|thousand|[bmk])?\b/gi;

function parseMoneyMention(numText: string, unit: string | undefined): number | null {
  const base = Number(numText.replace(/,/g, ""));
  if (!Number.isFinite(base)) return null;
  const u = (unit ?? "").toLowerCase();
  if (u === "b" || u === "billion") return base * 1_000_000_000;
  if (u === "m" || u === "million") return base * 1_000_000;
  if (u === "k" || u === "thousand") return base * 1_000;
  return base;
}

/** Mine labeled money amounts from free text using keyword windows. */
export function mineMetricsFromProse(prose: string): TSummaryMetric[] {
  const found = new Map<string, TSummaryMetric>();

  for (const match of prose.matchAll(MONEY_MENTION)) {
    const numeric = parseMoneyMention(match[1] ?? "", match[2]);
    if (numeric == null || match.index == null) continue;

    const windowStart = Math.max(0, match.index - 80);
    const windowEnd = Math.min(prose.length, match.index + match[0].length + 80);
    const window = prose.slice(windowStart, windowEnd);

    for (const pattern of MINED_PATTERNS) {
      if (found.has(pattern.id)) continue;
      if (pattern.keywords.test(window)) {
        found.set(pattern.id, {
          id: pattern.id,
          label: pattern.label,
          display: formatMoney(numeric),
          concept: pattern.concept,
          numeric,
          importance: pattern.importance,
        });
        break; // one concept per mention
      }
    }
  }

  return [...found.values()];
}

/* ------------------------------------------------------------------ */
/* Entry                                                               */
/* ------------------------------------------------------------------ */

export function extractMetrics(nodes: TSemanticNode[], prose: string | null): TSummaryMetric[] {
  const metrics: TSummaryMetric[] = [];
  collectMetricNodes(nodes, metrics);
  if (prose) {
    const mined = mineMetricsFromProse(prose);
    // Structured metrics win over mined ones for the same concept+label
    const seen = new Set(metrics.map((m) => `${m.concept}:${m.label.toLowerCase()}`));
    for (const metric of mined) {
      if (!seen.has(`${metric.concept}:${metric.label.toLowerCase()}`)) metrics.push(metric);
    }
  }
  return metrics.sort((a, b) => b.importance - a.importance).slice(0, 6);
}
